import React from 'react'

import {
    Await,
    Navigate,
    useAsyncError,
    useLoaderData,
    type ActionFunctionArgs,
    type LoaderFunctionArgs,
    type RouteObject
} from 'react-router-dom'

import ErrorComponentLoading from '@/components/base/errorComponentLoading'
import { warmChunk } from '@/lib/routeChunks'

/**
 * Screens open on tap. On a first visit a route's code and its loader run
 * behind the screen instead of in front of it (as React Router's `lazy`
 * would hold the old screen up): the page frame and the screen's skeleton
 * render at once, and the real screen replaces the skeleton when both have
 * arrived.
 *
 * Once a route's code is loaded its loader runs before the commit. Loaders
 * only make query handles (they await no network), so that costs nothing,
 * and a revisit served from the store renders the screen itself on the
 * first frame.
 */

export type RouteParts = {
    Component: React.ComponentType
    loader?: (args: LoaderFunctionArgs) => unknown
    action?: (args: ActionFunctionArgs) => unknown
}

/** No shell above: the screen is rendered as a plain route (tests, Home). */
const NO_SHELL = Symbol('no route shell')
const RouteDataContext = React.createContext<unknown>(NO_SHELL)

/**
 * The route's loader data, for screens rendered through shellRoute (in
 * place of useLoaderData, which there holds the shell's wrapper). Outside
 * a shell it is plain useLoaderData, so a screen also works as an ordinary
 * route.
 */
export function useRouteData<
    L extends (...args: never[]) => unknown = () => unknown
>(): Awaited<ReturnType<L>> {
    const shell = React.useContext(RouteDataContext)
    const router = useLoaderData()
    return (shell === NO_SHELL ? router : shell) as Awaited<ReturnType<L>>
}

/**
 * After an action, loaders do not run again: the action's write has
 * invalidated what it changed, and the screens showing it refetch from the
 * store. Navigations (and a retry) still run them.
 */
export const revalidateOnNavigation: NonNullable<
    RouteObject['shouldRevalidate']
> = ({ formMethod, defaultShouldRevalidate }) =>
    formMethod && formMethod.toUpperCase() !== 'GET'
        ? false
        : defaultShouldRevalidate

/**
 * revalidateOnNavigation, also skipping navigations that change only
 * `params` (a day picker in the URL): the screen already shows that data.
 */
export function revalidateUnlessOnly(
    ...params: string[]
): NonNullable<RouteObject['shouldRevalidate']> {
    const rest = (url: URL) => {
        const search = new URLSearchParams(url.search)
        for (const param of params) search.delete(param)
        return url.pathname + '?' + search.toString()
    }
    return (args) =>
        rest(args.currentUrl) === rest(args.nextUrl) &&
        args.currentUrl.href !== args.nextUrl.href
            ? false
            : revalidateOnNavigation(args)
}

function isRedirect(value: unknown): value is Response {
    return (
        value instanceof Response &&
        value.status >= 300 &&
        value.status < 400 &&
        !!value.headers.get('Location')
    )
}

/** A loader that redirected while its screen was already up. */
function Redirect({ response }: { response: Response }) {
    return (
        <Navigate
            to={response.headers.get('Location')!}
            replace={response.headers.get('X-Remix-Replace') === 'true'}
        />
    )
}

/**
 * A deferred loader that failed: a redirect it threw (e.g. a coach-only
 * screen opened by someone else) is followed; anything else goes to the
 * shared loader error UI.
 */
function RouteLoadError() {
    const error = useAsyncError()
    if (isRedirect(error)) return <Redirect response={error} />
    return <ErrorComponentLoading />
}

type ShellData = { data: unknown } | { route: Promise<unknown> }

function Screen({ parts, data }: { parts: () => RouteParts; data: unknown }) {
    if (isRedirect(data)) return <Redirect response={data} />
    const { Component } = parts()
    return (
        <RouteDataContext.Provider value={data}>
            <Component />
        </RouteDataContext.Provider>
    )
}

function RouteBody({
    parts,
    skeleton
}: {
    parts: () => RouteParts
    skeleton: React.ReactNode
}) {
    const shell = useLoaderData() as ShellData
    // One tree for both: <Await> renders a plain value at once, so a screen
    // that opened deferred is not remounted when its loader next runs on
    // the loaded path.
    return (
        <React.Suspense fallback={skeleton}>
            <Await
                resolve={'data' in shell ? shell.data : shell.route}
                errorElement={<RouteLoadError />}
            >
                {(data: unknown) => <Screen parts={parts} data={data} />}
            </Await>
        </React.Suspense>
    )
}

/**
 * A route whose screen shows at once. `load` imports the route module (its
 * chunk); `pick` takes the screen and its loader/action from it; `frame`
 * wraps the screen (the page with its header); `skeleton` stands in for
 * the screen while its code or data are on the way. `warm` fetches the
 * code ahead while idle (warmRouteChunks), so even the first open takes
 * the loaded path.
 */
export function shellRoute<M>({
    load,
    pick,
    frame,
    skeleton,
    warm = false,
    shouldRevalidate = revalidateOnNavigation
}: {
    load: () => Promise<M>
    pick: (module: M) => RouteParts
    frame: (body: React.ReactElement) => React.ReactElement
    skeleton: React.ReactNode
    warm?: boolean
    shouldRevalidate?: RouteObject['shouldRevalidate']
}): Pick<RouteObject, 'loader' | 'action' | 'element' | 'shouldRevalidate'> {
    let parts: RouteParts | undefined
    const ensure = () =>
        parts
            ? Promise.resolve(parts)
            : load().then((module) => (parts = pick(module)))
    if (warm) warmChunk(ensure)
    // Read only once `parts` is set: the loaded path, or a resolved route.
    const loaded = () => parts!

    return {
        loader: async (args): Promise<ShellData> => {
            if (parts) {
                return { data: parts.loader ? await parts.loader(args) : null }
            }
            // First visit: open now, load code and data behind the skeleton.
            const route = ensure().then((p) =>
                p.loader ? p.loader(args) : null
            )
            route.catch(() => undefined)
            return { route }
        },
        action: async (args) => {
            const p = await ensure()
            return p.action ? p.action(args) : null
        },
        shouldRevalidate,
        element: frame(<RouteBody parts={loaded} skeleton={skeleton} />)
    }
}
